import { Navigate, useOutletContext } from 'react-router-dom'

import { useQuery } from '@apollo/client'
import { Box } from '@factorialco/deprecated-design-system'
import { SurveyAnswerPageEmployeeContractVersionsDocument } from 'generated/resources/graphql'
import moment from 'moment'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useStrictParams } from 'modules/core/hooks/useStrictParams'
import { dateToString } from 'modules/core/lib/dates'
import { questionGroupRespondent } from 'modules/core/store/collections/users'

import { isManager } from 'modules/employees/lib/isManager'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'
import SurveyAnswer from 'modules/surveys/components/Answer/SurveyAnswer'
import SurveyShowHeader from 'modules/surveys/components/Show/SurveyShowHeader'
import SurveyShowPageTabs from 'modules/surveys/components/Show/SurveyShowPageTabs'
import { useQuestionGroup } from 'modules/surveys/hooks/useQuestionGroup'
import { useSurveyShowContext } from 'modules/surveys/pages/Show/hooks'
import type SurveyForm from 'modules/surveys/store/models/Form'
import type QuestionGroup from 'modules/surveys/store/models/QuestionGroup'
import memberships from 'modules/teams/store/collections/memberships'
import teams from 'modules/teams/store/collections/teams'
import { userIsTeamLead } from 'modules/teams/store/userIsTeamLead'

const RedirectToResponses = () => {
  const { navigation } = useNavigation()
  const { id } = useStrictParams<'id'>()
  const to = navigation.engagement.surveys.id.responses.fullPath({ id })

  return <Navigate to={to} />
}

const AnswerPage = () => {
  const {
    employee,
    access: { id: userId },
  } = useCurrentSession()
  const { survey, canEditSurvey } = useSurveyShowContext()
  const { group } = useOutletContext<{ form: SurveyForm; group?: QuestionGroup }>()
  const id = group?.attributes.id

  const { loading, questionGroup, refresh } = useQuestionGroup(Number(id))

  if (!employee) {
    throw new Error('Employee is required')
  }

  const { data: contractVersionsData, loading: loadingContractVersions } = useQuery(
    SurveyAnswerPageEmployeeContractVersionsDocument,
    {
      variables: {
        employeeId: employee.id,
        activeOn: dateToString(moment(), 'backend'),
      },
    }
  )

  const jobTitle =
    contractVersionsData?.contracts?.contractVersionsConnection?.nodes[0]?.jobTitle || null

  if (!survey || !questionGroup) {
    return null
  }

  const isQuestionGroupRespondent = questionGroup
    ? questionGroupRespondent(questionGroup)?.id === userId
    : false

  const canSeeInfo =
    survey.attributes.visible_to_managers &&
    (isManager(employee) || userIsTeamLead(teams, memberships, employee.id))

  if (!isQuestionGroupRespondent && canSeeInfo) return <RedirectToResponses />

  return (
    <ResponsivePage.Body
      showHeader={false}
      loading={loading || loadingContractVersions}
      error={null}
      authorized={isQuestionGroupRespondent}
    >
      <Box gap="s24">
        <SurveyShowHeader survey={survey} canEditSurvey={canEditSurvey} />
        <SurveyShowPageTabs form={survey} canEditSurvey={canEditSurvey} />
      </Box>
      <SurveyAnswer
        loading={loading}
        questionGroup={questionGroup}
        onDraftSaved={refresh}
        form={survey}
        canEditSurvey={canEditSurvey}
        jobTitle={jobTitle}
      />
    </ResponsivePage.Body>
  )
}

export default AnswerPage
